import Router from 'next/router'
import { Dispatch } from 'redux'

// @ts-ignore
import apiRequest from '../api/request'
// @ts-ignore
import { getJsonApiClient, setEntityModel } from '../api/apiClient'
import {
  getSuccessFlashMessages,
  getFailureFlashMessages
  // @ts-ignore
} from '../flashMessage'
// @ts-ignore
import getParamFromURL from '../getParamFromUrl'
import {
  actionCreators as listActionCreators,
  getActions as listGetActions,
  initialState as listInitialState,
  default as listReducer
} from '../list'
import ListItem from './ListItem'
// @ts-ignore
import { chauffeurDocs, lspDocs, vehicleDocs } from '../routes'
import mapListStateToProps from '../list/mapStateToProps'
import {
  AsyncAction
  // @ts-ignore
} from 'types/Action'
import {
  Document,
  DocumentType,
  DocumentListItemProps,
  UploadedDocumentListItemProps,
  ReadUploadedDocument,
  WriteUploadedDocument
} from 'types/Docs'
import { AllowedIncludeEntity, Entity } from 'types/Entity'
// @ts-ignore
import { ErrorResponse, OneToOneRelationship } from 'types/JsonApi'
import { Actions, State, ListStateToProps, List } from 'types/List'
import { LinkCreator, NavLink, Link } from 'types/Link'
import { Role } from 'types/Role'

// Types
interface MapDocsStateToProps {
  getTabs?: (roles: Role[], resourceId: string) => NavLink[]
  listPage: LinkCreator
  section: List
  state: {
    docs: State<Document>
    me: any
  }
}

interface ExtraListProps {
  disableBackButton?: boolean
}

// Constants
export const ENTITY: Entity = 'document'
export const INCLUDE_ENTITY: AllowedIncludeEntity = 'uploaded_document'
export const flashMessages = {
  success: (_: any) => getSuccessFlashMessages('docs.upload.success'),
  failure: (payload: ErrorResponse) =>
    getFailureFlashMessages('docs.upload.failure', payload)
}
const allowedFileTypes = [
  'image/jpg',
  'image/jpeg',
  'image/png',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/pdf'
]
const tableHeaders = [
  'shared.name',
  'shared.expiry-date-title',
  'shared.status',
  'shared.empty-string'
]

// Actions
export const actions = listGetActions(ENTITY)

// Model and initial state
export const Model: Document = {
  description: null,
  id: '',
  salesforce_document_id: '',
  name: '',
  expiry_date_required: false,
  document_type: 'driver',
  uploaded_document: {
    jsonApi: 'hasOne',
    type: INCLUDE_ENTITY
  }
}

export const getReadUploadedDocumentModel = (
  documentType: DocumentType
): ReadUploadedDocument => ({
  document_type: documentType,
  expiry_date: '',
  file_url: '',
  owner_uuid: '',
  rejected_reason: '',
  salesforce_document_id: '',
  status: 'pending',
  uuid: ''
})

export const initialState: State<Document> = Object.assign(
  {},
  listInitialState,
  {
    filter: {},
    resetWhenNotFound: true
  }
)

// Reducer
export default function reducer (
  state: State<Document> = initialState,
  action: Actions<Document>
) {
  return listReducer(actions, state, action)
}

// Action creators
export const actionCreators = {
  uploadDocument (payload: WriteUploadedDocument): AsyncAction {
    const backToOverviewLink = getDocumentListByDocumentType(
      payload.document_type,
      payload.owner_uuid
    )
    return (dispatch: Dispatch) =>
      apiRequest({
        apiAction: 'post',
        apiModel: payload,
        apiUrl: getJsonApiClient().all(INCLUDE_ENTITY),
        dispatch,
        entityName: ENTITY,
        flashMessages
      }).then((data: any) => {
        const { href, as } = backToOverviewLink
        if (!data.payload.errors) {
          Router.push(href, as)
        }
        if (Promise.resolve(data)) {
          return Promise.resolve(data)
        } else {
          return Promise.reject('An Error has ocurred')
        }
      })
  },
  loadDocs ({
    dispatch,
    entity,
    resourceId,
    state
  }: {
    dispatch: Dispatch
    entity: Entity
    resourceId: string
    state: any
  }) {
    return listActionCreators.loadList({
      apiUrl: getJsonApiClient()
        .one(entity, resourceId)
        .all(ENTITY),
      dispatch,
      entityName: ENTITY,
      include: [INCLUDE_ENTITY],
      state
    })
  }
}

// Side effects
const uploadedDocumentSerializer = (
  uploadedDocument: WriteUploadedDocument
) => ({
  type: 'uploaded_documents',
  attributes: {
    salesforce_document_id: uploadedDocument.salesforce_document_id,
    owner_uuid: uploadedDocument.owner_uuid,
    document_type: uploadedDocument.document_type,
    expiry_date: uploadedDocument.expiry_date,
    data_url: uploadedDocument.data_url
  }
})

setEntityModel(ENTITY, Model)
setEntityModel(
  INCLUDE_ENTITY,
  getReadUploadedDocumentModel('' as DocumentType),
  {
    serializer: uploadedDocumentSerializer
  }
)

export function checkFile (parsedFile: Blob | null) {
  if (parsedFile) {
    const maxFileSize = 10000000 // 10Mb in bytes
    const isFileSizeAllowed = parsedFile.size <= maxFileSize
    const isValidFileType = allowedFileTypes.includes(parsedFile.type)
    return isValidFileType && isFileSizeAllowed
  }
  return false
}

export function getListItemProps (
  item: Document
): UploadedDocumentListItemProps | DocumentListItemProps {
  const parentResourceId = getParamFromURL('id')
  const props: DocumentListItemProps = {
    id: item.id,
    parentResourceId,
    name: item.name,
    expiryDate: item.expiry_date_required,
    status: null,
    url: null
  }
  const { uploaded_document: uploadedDocument } = item
  if (uploadedDocument && uploadedDocument.status) {
    if (uploadedDocument.expiry_date) {
      props.expiryDate = uploadedDocument.expiry_date
    }
    props.status = uploadedDocument.status
    props.url = uploadedDocument.file_url
  }
  return props
}

export function mapDocsStateToProps ({
  getTabs,
  listPage,
  section,
  state,
  ...extra
}: MapDocsStateToProps & ExtraListProps): ListStateToProps<Document> {
  const resourceId = getParamFromURL('id')

  return mapListStateToProps(
    state.docs,
    {
      disableBackButton: extra.disableBackButton,
      itemComponent: ListItem,
      keyPrefix: section,
      listPage: listPage(),
      resourceId,
      tableHeaders
    },
    typeof getTabs === 'function' && resourceId
      ? getTabs(state.me.roles, resourceId)
      : undefined
  )
}

function getDocumentListByDocumentType (
  documentType: DocumentType,
  id: string
): Link {
  let linkCreator
  switch (documentType) {
    case 'driver':
      linkCreator = chauffeurDocs
      break
    case 'vehicle':
      linkCreator = vehicleDocs
      break
    case 'company':
      linkCreator = lspDocs
      break
    default:
      throw new Error('no valid document type found')
  }
  return linkCreator({ qs: { id } })
}
